import { Form } from '@epages/react-components'
import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'
import Immutable from 'immutable'
import cc from 'classcat'

import { formatHref } from '../../utils/link-sanitizer'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import ImageFieldMultiple from '../imagegallery/ImageFieldMultiple'

type TFooterInfo = 'success' | 'delete' | 'empty' | 'full' | 'count'

export const MAXIMUM_NUMBER_OF_IMAGES = 24

function removeLocalPreview(images: ImmutableMap) {
  images.forEach((i) => delete i.localPreview)
  return images
}

type ImageGallerySettingsProps = WorkspacePluginSettingsProps & {
  onBusyStateChange: (isBusy: boolean) => void
}

export default function ImageGallerySettings({
  config,
  data,
  onCancel,
  onSave,
  onDataChange,
  onBusyStateChange,
  renderLayout,
}: Readonly<ImageGallerySettingsProps>): ReactElement {
  const [footerInfo, setFooterInfo] = useState<TFooterInfo>('count')
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false)
  const timerRef = useRef<number>(0)

  const t = useTranslate('interface', 'components.imageGalleryComponent.settingsModal')

  const imageCount = data.get('images')?.size || 0

  useEffect(() => {
    // if there's a timer running while a new notification is displayed then
    // prevent this old timer from hiding the new notification (too soon / instantly)
    if (timerRef.current) window.clearTimeout(timerRef.current)

    if (imageCount === 0 && footerInfo !== 'delete') setFooterInfo('empty')
    else if (imageCount > MAXIMUM_NUMBER_OF_IMAGES) setFooterInfo('full')
    else if (footerInfo !== 'count') {
      // start new cancelable timer
      timerRef.current = window.setTimeout(() => setFooterInfo('count'), 2500)
    }
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [footerInfo, imageCount])

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'success':
        return t('footerText.imageUploaded')
      case 'delete':
        return t('footerText.imageDeleted')
      case 'empty':
        return t('footerText.uploadImagesNotification', {
          maximumNumberOfUploadedImages: MAXIMUM_NUMBER_OF_IMAGES,
        })
      case 'count':
        return t('footerText.uploadedImages', {
          maximumNumberOfUploadedImages: MAXIMUM_NUMBER_OF_IMAGES,
          numberOfUploadedImages: imageCount,
        })
      case 'full':
        return t('footerText.uploadImagesMaximum', {
          maximumNumberOfUploadedImages: MAXIMUM_NUMBER_OF_IMAGES,
        })
      default:
        return ''
    }
  }

  const onChange = (data) => {
    onDataChange(data.update('images', (images) => removeLocalPreview(images)))
  }

  return (
    <Form
      name="pluginImageGallerySettings"
      value={data}
      onSubmit={onSave}
      onChange={onChange}
      normalize={handleBeforeSave}
    >
      {({ value }) =>
        renderLayout({
          title: imageCount > 0 ? t('imageSettings.title.onEdit') : t('imageSettings.title.onCreate'),
          body: (
            <scroll-shadow>
              <div className="dali-settingslayer-main-content">
                <ImageFieldMultiple
                  data={value}
                  setFooterInfo={setFooterInfo}
                  setIsSaveDisabled={setIsSaveDisabled}
                  onBusyStateChange={onBusyStateChange}
                  imageUrl={config?.imageUrl}
                  storeFile={config?.storeFile}
                  previewUrl={config?.previewUrl}
                  className="dali-settingslayer-imagegallery-dropzone"
                />
              </div>
            </scroll-shadow>
          ),
          footer: (
            <ButtonRow>
              <div
                className={cc(['dali-settingslayer-status', `dali-settingslayer-status-${footerInfo}`])}
                key={footerInfo}
              >
                {getStatusMessage(footerInfo)}
              </div>

              <Button variant="cancel" type="button" onClick={onCancel}>
                {t('cancelButton.label')}
              </Button>
              <Button disabled={isSaveDisabled}>{t('saveButton.label')}</Button>
            </ButtonRow>
          ),
        })
      }
    </Form>
  )
}

// Normalizes image data for save in the database
function handleBeforeSave(data) {
  return data.update('images', (images) =>
    images.map((image) =>
      Immutable.Map({
        src: image.get('src'),
        width: image.get('width'),
        height: image.get('height'),
        alt: image.get('alt'),
        link: image.get('link') ? formatHref(image.get('link')) : '',
        opentab: image.get('opentab'),
        position: image.get('position'),
      }),
    ),
  )
}
